import React, { useEffect, useState } from "react";
import { API } from "@/auth";
import { useI18n } from "@/i18n";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Building2, User, Eye, EyeOff } from "lucide-react";
import { CountrySelect, ProvinceSelect } from "@/components/GeoSelect";

const EMPTY = {
  tipo: "privado",
  fullname: "",
  email: "",
  password: "",
  telefono: "",
  telefono_alt: "",
  empresa: "",
  rfc_nit: "",
  documento_id: "",
  sector: "",
  persona_contacto: "",
  direccion: "",
  ciudad: "",
  provincia: "",
  pais: "",
  codigo_postal: "",
};

export default function Register() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [oficinas, setOficinas] = useState([]);
  const [provMap, setProvMap] = useState({});
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    // Public unauthenticated fetch attempt (uses public track for no-op); we skip since /oficinas needs auth.
    // We'll show the auto-branch preview only after login. Leave placeholder.
    setOficinas([]);
    setProvMap({});
    axios.get(`${API}/public/countries`).then((r) => setCountries(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isCompany = form.tipo === "empresa";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/register`, form);
      localStorage.setItem("meos_token", data.token);
      toast.success(t("saved"));
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const inp =
    "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100 transition";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";
  const previewOficina = provMap[form.provincia?.trim()] || "";

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-6">
          <img src="/logo.png" alt="Meos" className="h-20 mx-auto mb-4" />
          <h1 className="text-white font-bold text-2xl">{t("new_client")}</h1>
          <p className="text-slate-400 text-sm mt-1">{t("app_tagline")}</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-2xl">
          {/* Tipo cliente */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <button
              type="button"
              onClick={() => set("tipo", "privado")}
              data-testid="reg-type-private"
              className={`flex items-center justify-center gap-2 py-3 rounded-lg border-2 font-semibold text-sm transition ${
                !isCompany ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
            >
              <User className="w-4 h-4" /> {t("private")}
            </button>
            <button
              type="button"
              onClick={() => set("tipo", "empresa")}
              data-testid="reg-type-company"
              className={`flex items-center justify-center gap-2 py-3 rounded-lg border-2 font-semibold text-sm transition ${
                isCompany ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
            >
              <Building2 className="w-4 h-4" /> {t("company")}
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {/* Personal / Company info */}
            <div>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                {isCompany ? t("company_info") : t("personal_info")}
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>{isCompany ? t("company") : t("fullname")}</label>
                  <input
                    required
                    data-testid="reg-fullname"
                    value={form.fullname}
                    onChange={(e) => set("fullname", e.target.value)}
                    className={inp}
                  />
                </div>
                {isCompany && (
                  <div>
                    <label className={lbl}>{t("contact_person")}</label>
                    <input
                      data-testid="reg-contact-person"
                      value={form.persona_contacto}
                      onChange={(e) => set("persona_contacto", e.target.value)}
                      className={inp}
                    />
                  </div>
                )}
                {isCompany && (
                  <div>
                    <label className={lbl}>{t("rfc_nit")}</label>
                    <input
                      data-testid="reg-rfc-nit"
                      value={form.rfc_nit}
                      onChange={(e) => set("rfc_nit", e.target.value)}
                      className={inp}
                    />
                  </div>
                )}
                {isCompany && (
                  <div>
                    <label className={lbl}>{t("sector")}</label>
                    <input
                      data-testid="reg-sector"
                      value={form.sector}
                      onChange={(e) => set("sector", e.target.value)}
                      className={inp}
                    />
                  </div>
                )}
                {!isCompany && (
                  <div>
                    <label className={lbl}>
                      {t("doc_id")} <span className="text-slate-400 normal-case font-normal">({t("optional")})</span>
                    </label>
                    <input
                      data-testid="reg-doc-id"
                      value={form.documento_id}
                      onChange={(e) => set("documento_id", e.target.value)}
                      className={inp}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t("contact_info")}</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>{t("email")}</label>
                  <input
                    type="email"
                    required
                    data-testid="reg-email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className={inp}
                  />
                </div>
                <div>
                  <label className={lbl}>{t("password")}</label>
                  <div className="relative">
                    <input
                      type={showPass ? "text" : "password"}
                      required
                      minLength={6}
                      data-testid="reg-password"
                      value={form.password}
                      onChange={(e) => set("password", e.target.value)}
                      className={inp + " pe-10"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute end-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label={showPass ? t("hide_password") : t("show_password")}
                    >
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className={lbl}>{t("phone")}</label>
                  <input
                    data-testid="reg-phone"
                    value={form.telefono}
                    onChange={(e) => set("telefono", e.target.value)}
                    className={inp}
                  />
                </div>
                <div>
                  <label className={lbl}>
                    {t("phone_alt")} <span className="text-slate-400 normal-case font-normal">({t("optional")})</span>
                  </label>
                  <input
                    data-testid="reg-phone-alt"
                    value={form.telefono_alt}
                    onChange={(e) => set("telefono_alt", e.target.value)}
                    className={inp}
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t("address_info")}</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className={lbl}>{t("address")}</label>
                  <input
                    data-testid="reg-address"
                    value={form.direccion}
                    onChange={(e) => set("direccion", e.target.value)}
                    className={inp}
                  />
                </div>
                <div>
                  <label className={lbl}>{t("city")}</label>
                  <input
                    data-testid="reg-city"
                    value={form.ciudad}
                    onChange={(e) => set("ciudad", e.target.value)}
                    className={inp}
                  />
                </div>
                <div>
                  <label className={lbl}>{t("country")}</label>
                  <CountrySelect
                    countries={countries}
                    value={form.pais}
                    onChange={(v) => setForm((f) => ({ ...f, pais: v, provincia: "" }))}
                    className={inp}
                    testid="reg-country"
                  />
                </div>
                <div>
                  <label className={lbl}>{t("province")}</label>
                  <ProvinceSelect
                    countries={countries}
                    country={form.pais}
                    value={form.provincia}
                    onChange={(v) => set("provincia", v)}
                    className={inp}
                    testid="reg-province"
                  />
                </div>
                <div>
                  <label className={lbl}>{t("postal_code")}</label>
                  <input
                    data-testid="reg-postal"
                    value={form.codigo_postal}
                    onChange={(e) => set("codigo_postal", e.target.value)}
                    className={inp}
                  />
                </div>
              </div>
              {previewOficina && (
                <p className="text-xs text-emerald-700 mt-2 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                  ✓ {t("assigned_branch")}: <strong>{previewOficina}</strong>
                </p>
              )}
            </div>

            <button
              disabled={busy}
              data-testid="reg-submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg mt-2 disabled:opacity-60 transition"
            >
              {busy ? "..." : t("save")}
            </button>
          </form>

          <p className="text-center text-xs text-slate-500 mt-4">
            <Link to="/login" className="text-red-600 font-semibold hover:underline">
              {t("login_title")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
